import React, { useState } from 'react';
import { Modal } from 'antd';
import { submitSupportRequest } from '../../../api';
import messageQueue from '../../../utils/messageQueue';
import MessageConstant from '../../../utils/Constant/MessageConstant';
import styles from './SupportRequestModal.module.scss';

const SupportRequestModal = ({ visible, onClose }) => {
    const [category, setCategory] = useState('');
    const [priority, setPriority] = useState('normal');
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const categories = [
        { value: 'technical', label: 'Technical Issue', icon: '⚙️' },
        { value: 'account', label: 'Account Help', icon: '👤' },
        { value: 'billing', label: 'Billing Question', icon: '💳' },
        { value: 'feature', label: 'Feature Request', icon: '✨' },
        { value: 'other', label: 'Other', icon: '💬' }
    ];

    const priorities = [
        { value: 'low', label: 'Low - General question', color: '#16a34a' },
        { value: 'normal', label: 'Normal - Need assistance', color: '#220ec9' },
        { value: 'high', label: 'High - Urgent help needed', color: '#f59e0b' }
    ];

    const handleCancel = () => {
        setCategory('');
        setPriority('normal');
        setSubject('');
        setDescription('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!category || !subject || !description) {
            messageQueue.warning('VALIDATION_REQUIRED_FIELDS', MessageConstant.VALIDATION_REQUIRED_FIELDS);
            return;
        }

        setIsSubmitting(true);
        try {
            await submitSupportRequest('support', {
                category,
                priority,
                subject,
                description,
                currentPage: window.location.pathname,
                currentUrl: window.location.href,
                userAgent: navigator.userAgent,
            });
            messageQueue.success('SUPPORT_REQUEST_SUCCESS', MessageConstant.SUPPORT_REQUEST_SUCCESS);
            handleCancel();
        } catch (error) {
            messageQueue.error('SUPPORT_REQUEST_ERROR', error.response?.data?.error || 'Failed to submit support request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            centered
            width={700}
            className={styles.supportModal}
        >
            <div className={styles.modalContent}>
                {/* Header */}
                <div className={styles.header}>
                    <svg className={styles.supportIcon} width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="24" cy="24" r="20" fill="#EEF2FF" />
                        <path d="M24 34C29.5228 34 34 29.5228 34 24C34 18.4772 29.5228 14 24 14C18.4772 14 14 18.4772 14 24C14 25.38 14.345 26.6824 14.965 27.825L14 34L20.175 33.035C21.3176 33.655 22.62 34 24 34Z" stroke="#220ec9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M20 22H28M20 26H24" stroke="#220ec9" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <h2 className={styles.title}>Request Support</h2>
                    <p className={styles.subtitle}>Our team is here to help you with any questions or issues</p>
                </div>

                {/* Category Selection */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Support Category <span className={styles.required}>*</span></label>
                    <div className={styles.categoryGrid}>
                        {categories.map((cat) => (
                            <div
                                key={cat.value}
                                className={`${styles.categoryCard} ${category === cat.value ? styles.selected : ''}`}
                                onClick={() => setCategory(cat.value)}
                            >
                                <span className={styles.categoryIcon}>{cat.icon}</span>
                                <span className={styles.categoryLabel}>{cat.label}</span>
                                <div className={styles.checkmark}>✓</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Priority */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Priority Level</label>
                    <div className={styles.priorityList}>
                        {priorities.map((p) => (
                            <div
                                key={p.value}
                                className={`${styles.priorityItem} ${priority === p.value ? styles.selected : ''}`}
                                onClick={() => setPriority(p.value)}
                                style={{ '--priority-color': p.color }}
                            >
                                <div className={styles.radioButton}>
                                    {priority === p.value && <div className={styles.radioInner} />}
                                </div>
                                <span className={styles.priorityText}>{p.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Subject */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Subject <span className={styles.required}>*</span></label>
                    <input
                        type="text"
                        className={styles.textInput}
                        placeholder="Brief summary of your issue or question"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                    />
                </div>

                {/* Description */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Description <span className={styles.required}>*</span></label>
                    <textarea
                        className={styles.textArea}
                        placeholder="Please provide detailed information about your request..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={5}
                    />
                </div>

                {/* Info Box */}
                <div className={styles.infoBox}>
                    <svg className={styles.infoIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10" r="8" stroke="#220ec9" strokeWidth="1.5" />
                        <path d="M10 9V13M10 7V7.5" stroke="#220ec9" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <div className={styles.infoText}>
                        <strong>Response Time:</strong>
                        <span>Our support team typically responds within 24 hours during business days.</span>
                    </div>
                </div>

                {/* Actions */}
                <div className={styles.actions}>
                    <button className={styles.cancelButton} onClick={handleCancel}>
                        Cancel
                    </button>
                    <button
                        className={styles.submitButton}
                        onClick={handleSubmit}
                        disabled={!category || !subject || !description || isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default SupportRequestModal;
